import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { PRZEDMIOT_PO_SLUGU, LITERY, LICENCJA_PO_SLUGU, kluczeOdpowiedzi } from '../data/licencje';
import { pobierzPytanie, pobierzPrzedmiot } from '../data/pytania';
import MarkdownRenderer from './MarkdownRenderer';
import JsonLd from '../seo/JsonLd';
import { absoluteUrl, breadcrumbJsonLd, quizJsonLd } from '../seo/siteMeta';
import styles from '../css/modules/Nauka.module.css';

// Ile pokrewnych pytań pokazujemy pod omówieniem. Sześć wystarczy, żeby dać
// robotowi ścieżkę i czytelnikowi sensowny wybór, a nie rozdmuchuje strony.
const ILE_POKREWNYCH = 6;

/**
 * Strona pojedynczego pytania: /pytania/<slug>.
 *
 * To główne źródło ruchu long-tail - ktoś wpisuje w wyszukiwarkę treść pytania
 * i trafia na stronę z odpowiedzią, omówieniem i podstawą prawną. Dlatego
 * treść musi być w HTML już po prerenderze, a nie dopiero po hydratacji.
 */
const QuestionPage = () => {
  const { slug } = useParams();
  const [pytanie, setPytanie] = useState(null);
  const [pokrewne, setPokrewne] = useState([]);
  const [status, setStatus] = useState('ladowanie');

  useEffect(() => {
    let anulowane = false;
    setStatus('ladowanie');

    // Treść bierzemy z lokalnych plików generowanych przez scripts/pytania/gen.py,
    // a nie z API. Prerender zrzuca DOM po budżecie czasu wirtualnego, który nie
    // czeka na odpowiedź z zewnętrznego hosta - przy fetchu strony pytań
    // zapisywały się puste. Dział dociąga się osobnym plikiem, więc odwiedzający
    // pobiera tylko swój fragment bazy.
    pobierzPytanie(slug)
      .then(d => {
        if (anulowane) return;
        if (!d) {
          setStatus('brak');
          return;
        }
        setPytanie(d);
        setStatus('ok');

        // Pokrewne pytania z tego samego przedmiotu. Chunk przedmiotu jest już
        // pobrany (to z niego przyszło bieżące pytanie), więc lista nic nie
        // kosztuje w transferze, a rozwiązuje najdroższy problem SEO serwisu:
        // do stron /pytania/* nie prowadził wcześniej ANI JEDEN link.
        return pobierzPrzedmiot(d.grupa).then(wszystkie => {
          if (anulowane) return;
          setPokrewne(wszystkie.filter(p => p.slug !== d.slug).slice(0, ILE_POKREWNYCH));
        });
      })
      .catch(() => !anulowane && setStatus('brak'));

    return () => {
      anulowane = true;
    };
  }, [slug]);

  if (status === 'ladowanie') {
    return <div className={styles.wrap}><p className={styles.info}>Wczytujemy pytanie…</p></div>;
  }

  if (status === 'brak' || !pytanie) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie znaleźliśmy tego pytania</h1>
        <p className={styles.info}>Możliwe, że adres jest nieaktualny.</p>
        <Link className={styles.cta} to="/">Wróć na stronę główną</Link>
      </div>
    );
  }

  const klucze = kluczeOdpowiedzi(pytanie);
  const grupa = PRZEDMIOT_PO_SLUGU[pytanie.grupa];
  const licencje = (pytanie.licencje || []).map(l => LICENCJA_PO_SLUGU[l]).filter(Boolean);
  const glowna = licencje[0];
  const poprawna = pytanie[pytanie.correctAnswer];

  const tytul = `${pytanie.question} - odpowiedź i wyjaśnienie`;
  const opis = `${pytanie.question} Poprawna odpowiedź: ${poprawna}. ${
    (pytanie.explanation || '').slice(0, 120)
  }`.replace(/\s+/g, ' ').trim().slice(0, 300);

  return (
    <div className={styles.wrap}>
      <Helmet>
        <title>{tytul}</title>
        <meta name="description" content={opis} />
        <link rel="canonical" href={absoluteUrl(`/pytania/${slug}`)} />
      </Helmet>

      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        ...(glowna ? [{ name: glowna.nazwa, path: glowna.path }] : []),
        { name: 'Pytanie', path: `/pytania/${slug}` },
      ])} />
      <JsonLd data={quizJsonLd({
        slug,
        question: pytanie.question,
        warianty: klucze.map(k => pytanie[k]),
        poprawna,
        wyjasnienie: pytanie.explanation,
        przedmiot: grupa ? grupa.nazwa : '',
        licencje: licencje.map(l => l.nazwa),
      })} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link>
        {glowna && <> / <Link to={glowna.path}>{glowna.nazwa}</Link></>}
        {grupa && glowna && <> / <Link to={`/nauka/${glowna.slug}/${grupa.slug}`}>{grupa.nazwa}</Link></>}
      </nav>

      <h1 className={styles.h1}>{pytanie.question}</h1>

      <div className={styles.karta}>
        <div className={styles.odpowiedzi}>
          {klucze.map(k => (
            <div key={k} className={`${styles.odpowiedz} ${k === pytanie.correctAnswer ? styles.dobra : ''}`}>
              <span className={styles.litera}>{LITERY[k]}</span>
              <span>{pytanie[k]}</span>
            </div>
          ))}
        </div>

        <div className={styles.omowienie}>
          <p className={styles.werdyktOk}>Poprawna odpowiedź: {LITERY[pytanie.correctAnswer]}</p>
          {pytanie.explanation && <MarkdownRenderer content={pytanie.explanation} />}
          {pytanie.legal_basis && <p className={styles.podstawa}>Podstawa: {pytanie.legal_basis}</p>}

          {/* Krajowy Bank Pytań ULC nie jest publikowany, więc mówimy wprost, że
              pytanie jest autorskie - użytkownik ma wiedzieć, czego się uczy. */}
          <p className={styles.zastrzezenie}>
            Pytanie autorskie, napisane pod program egzaminu teoretycznego. Nie pochodzi
            z Krajowego Banku Pytań ULC, który nie jest udostępniany publicznie. Jeżeli
            uważasz, że odpowiedź wymaga korekty, napisz do nas - sprawdzimy i poprawimy.
          </p>
        </div>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Gdzie to pytanie się pojawia</h2>
        <p>
          {grupa ? `Przedmiot: ${grupa.nazwa}. ` : ''}
          {licencje.length
            ? `Zakres obowiązujący na egzaminie na ${licencje.map(l => l.nazwa).join(', ')}.`
            : ''}
        </p>
      </div>

      {pokrewne.length > 0 && (
        <div className={styles.tresc}>
          <h2 className={styles.h2}>
            Podobne pytania{grupa ? ` z przedmiotu ${grupa.nazwa.toLowerCase()}` : ''}
          </h2>
          <ul className={styles.listaPokrewnych}>
            {pokrewne.map(p => (
              <li key={p.slug}>
                <Link to={`/pytania/${p.slug}`}>{p.question}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.akcje}>
        <Link className={styles.cta} to="/intro">
          Sprawdź się w darmowym teście
        </Link>
        {grupa && glowna && (
          <Link className={styles.ctaDrugi} to={`/nauka/${glowna.slug}/${grupa.slug}`}>
            Więcej pytań z przedmiotu {grupa.nazwa.toLowerCase()}
          </Link>
        )}
      </div>
    </div>
  );
};

export default QuestionPage;
